/**
 * Копии, лежащие файлами на устройстве.
 *
 * В приложении копия — это настоящий файл в документах телефона, а не запись
 * внутри приложения. Разница принципиальная: снимок внутри исчезает вместе с
 * приложением, файл переживает и удаление, и переустановку, и его видно из
 * файлового менеджера.
 *
 * Файлы складываются в свою папку — «Документы/Kairo». Отдельная папка нужна
 * не для порядка ради порядка: по ней человек находит копии в телефоне, её же
 * можно целиком отправить в облако, и в неё не попадает ничего чужого.
 *
 * ## Почему список, а не одна последняя копия
 *
 * Беда, от которой спасает копия, бывает замечена не сразу: ошибочную замену
 * данных или пропажу части записей обнаруживают через несколько дней. Одна
 * последняя копия к этому моменту уже содержит ту же беду. Поэтому копий
 * несколько, они прорежаются по тому же правилу, что и снимки внутри
 * приложения, и каждую можно и отправить в облако, и восстановить.
 */

import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

import type { IsoDate } from '@/shared/lib/datetime';

import { backupFileName, type Backup } from './export-data';
import { planRetention } from './retention';

/** Папка внутри документов устройства. */
export const BACKUP_FOLDER = 'Kairo';

/** Человеческое название места — его показывают в настройках. */
export const BACKUP_FOLDER_LABEL = 'Документы/Kairo';

export interface BackupFile {
  /** Путь относительно документов: по нему файл читается и удаляется. */
  path: string;
  name: string;
  date: IsoDate;
  /** Размер в байтах — по нему видно, что копия не пустая. */
  size: number;
  /** Ссылка для системной отправки: в облако, в почту, куда угодно. */
  uri: string;
}

/**
 * Умеет ли устройство хранить копии файлами.
 *
 * Во вкладке браузера — нет: записать файл на диск без спроса нельзя, и копии
 * там остаются снимками внутри приложения.
 */
export function areBackupFilesSupported(): boolean {
  return Capacitor.isNativePlatform();
}

const NAME_PATTERN = /^kairo-backup-(\d{4}-\d{2}-\d{2})\.json$/;

/**
 * Копии, лежащие на устройстве, — от свежей к старой.
 *
 * Смотрим и в своей папке, и в корне документов: там лежат копии, снятые до
 * появления папки, и делать вид, что их нет, нельзя — человек рассчитывает
 * именно на них.
 */
export async function listBackupFiles(): Promise<BackupFile[]> {
  if (!areBackupFilesSupported()) return [];

  const found = new Map<string, BackupFile>();

  for (const folder of [BACKUP_FOLDER, '']) {
    for (const file of await readFolder(folder)) {
      // Одноимённый файл в папке важнее лежащего в корне: он новее.
      if (!found.has(file.name) || folder === BACKUP_FOLDER) found.set(file.name, file);
    }
  }

  return [...found.values()].sort((a, b) => b.date.localeCompare(a.date));
}

async function readFolder(folder: string): Promise<BackupFile[]> {
  try {
    const { files } = await Filesystem.readdir({ path: folder, directory: Directory.Documents });

    return files.flatMap((file) => {
      const date = NAME_PATTERN.exec(file.name)?.[1];
      if (file.type !== 'file' || date === undefined) return [];

      return [
        {
          path: folder === '' ? file.name : `${folder}/${file.name}`,
          name: file.name,
          date: date,
          size: file.size,
          uri: file.uri,
        },
      ];
    });
  } catch {
    // Папки может не быть вовсе — это обычное состояние до первой копии, а не
    // сбой. Пустой список здесь честнее исключения.
    return [];
  }
}

/**
 * Пишет копию файлом в папку приложения и возвращает ссылку на неё.
 *
 * Текст приходит готовым — уже зашифрованным, если человек включил пароль.
 * Решать это здесь нельзя: путей наружу четыре, и каждый решающий сам однажды
 * останется незашифрованным.
 */
export async function writeBackupToFiles(backup: Backup, content: string): Promise<string> {
  const written = await Filesystem.writeFile({
    path: `${BACKUP_FOLDER}/${backupFileName(backup.exportedAt)}`,
    data: content,
    directory: Directory.Documents,
    encoding: Encoding.UTF8,
    recursive: true,
  });

  return written.uri;
}

/**
 * Читает файл копии с устройства — текстом, как есть.
 *
 * Именно текстом, а не разобранной копией: файл может быть закрыт паролем, и
 * решать, что с ним делать, должна одна общая точка, а не каждый список
 * копий по-своему.
 */
export async function readBackupFileAt(path: string): Promise<string> {
  const { data } = await Filesystem.readFile({
    path,
    directory: Directory.Documents,
    encoding: Encoding.UTF8,
  });

  return typeof data === 'string' ? data : '';
}

export async function deleteBackupFile(path: string): Promise<void> {
  await Filesystem.deleteFile({ path, directory: Directory.Documents });
}

/**
 * Отдаёт копию системному диалогу отправки.
 *
 * Отсюда она уходит в Google Диск, Яндекс.Диск, почту — куда человек привык
 * складывать важное. Приложение при этом никуда не подключается и ничего не
 * отправляет само: выбор адресата остаётся за человеком, а в копии лежат в том
 * числе записи о здоровье.
 *
 * `false` — диалог закрыли, не выбрав ничего. Это обычный отказ, а не сбой.
 */
export async function shareBackupFile(file: BackupFile): Promise<boolean> {
  try {
    await Share.share({
      title: 'Резервная копия Kairo',
      text: `Копия данных от ${file.date}`,
      url: file.uri,
      dialogTitle: 'Куда отправить копию',
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Убирает лишние копии по тому же правилу, что и снимки внутри приложения:
 * последняя неделя по дням, год по месяцам и три года по годам.
 *
 * Возвращает, сколько файлов удалено. Ошибка удаления не считается бедой:
 * файл могли убрать из файлового менеджера прямо сейчас.
 */
export async function pruneBackupFiles(): Promise<number> {
  const { drop } = planRetention(await listBackupFiles());

  let removed = 0;
  for (const file of drop) {
    try {
      await deleteBackupFile(file.path);
      removed += 1;
    } catch {
      // Файла уже нет — цель достигнута и без нас.
    }
  }

  return removed;
}
